/**
 * V2 Supplement (import results) page.
 *
 * Faithful TS+React+Tailwind port of design_reference/js/screens-supplement.jsx,
 * wired to the REAL parse pipeline:
 *   - BackendAPI.parseEstimate(file) -> ParsedEstimateResponse
 *   - BackendAPI.overrideLineItem(parse_hash, roomIndex, lineItemIndex, cat, sel)
 *     for inline CAT/SEL edits in List view (the real endpoint).
 *
 * "Faithful import first" — the page reproduces exactly what the parser found.
 * No prices are fabricated: each item's `total` is the carrier's line RCV
 * recovered from the PDF (B-159), or 0 when the source line carried no money
 * columns — so the review screen totals match the exported ESX.
 *
 * Real-shape notes (web/src/types/index.ts):
 *   - ParserDiagnostics.confidence is 0..1 (we render Math.round(c*100)).
 *   - ParsedRoom: { group_code, group_description, line_items[], headings? }.
 *   - ParsedLineItem: { line_number, category, selector, description, quantity,
 *       unit, activity?, group_code, group_description, provenance?, notes? }.
 */

import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { DragEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { BackendAPI } from '../../lib/api';
import type {
  DictionarySearchResult,
  CategoryOption,
  SelectorOption,
} from '../../lib/api';
import useAuthStore from '../../stores/authStore';
import useImportJobsStore from '../../stores/importJobsStore';
import { triggerEsxDownload } from '../../lib/esxDownload';
import { useCredits } from '../../hooks/useCredits';
import { useIsPayPerUser } from '../../hooks/useMembership';
import useProjectStore from '../../stores/projectStore';
import type {
  ApiError,
  EstimateHeaderMetadata,
  ImportSummary,
  ParsedEstimateResponse,
  ParsedLineItem,
  SourceRender,
  ParserDiagnostics,
  PreflightResponse,
  Project,
  ProjectMatchResponse,
  Provenance,
} from '../../types';

import { Banner } from '../components/ui/banner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { EmptyState } from '../components/ui/empty-state';
import { Modal } from '../components/ui/modal';
import { Pill } from '../components/ui/pill';
import { Segmented, type SegmentedOption } from '../components/ui/segmented';
import { Skeleton } from '../components/ui/skeleton';
import {
  IconCheck,
  IconDownload,
  IconList,
  IconFolder,
  IconRefresh,
  IconSpark,
  IconUpload,
  IconWarn,
} from '../components/icons';
import { ScopeTreeTable } from '../components/ScopeTreeTable';
import { OriginalOrEnhanced } from '../components/OriginalEnhancedToggle';
import { canShowOriginal, useOriginalView } from '../lib/useOriginalView';
import { PROV_META, activitySymbol, ACT_LABEL } from '../lib/semantics';
import { cn } from '../../lib/utils';
import PostParseProjectDialog from '../components/PostParseProjectDialog';
import PreImportDialog, { type PreImportDecision } from '../components/PreImportDialog';
import { preflightHeaderToNewProjectForm } from '../lib/projectFromHeader';
import EstimateTabs from '../components/EstimateTabs'; // PRP-INTAKE-1
import EnhancePanel from '../components/enhance/EnhancePanel';
import type { EnhanceSessionResponse } from '../../types';
import GetCreditsModal from '../components/GetCreditsModal';
import EstimateChangedNotice from '../components/EstimateChangedNotice';
import FieldProjectBanner from '../components/FieldProjectBanner';
import ExportCostDialog from '../components/ExportCostDialog';
import OpRatesField from '../components/OpRatesField'; // PRP-FEEDBACK-1 W1
import ImportNameField from '../components/ImportNameField'; // PRP-FEEDBACK-2 B-7515
import AddToProjectDialog from '../components/AddToProjectDialog'; // PRP-FEEDBACK-2 B-7516a
import RoofAttachCard from '../components/roof/RoofAttachCard'; // PRP-ROOF-7 W3
import NoteReviewPanel from '../components/NoteReviewPanel'; // PRP-NOTES-1
import ExportPriceListNote from '../components/ExportPriceListNote';
import ExportDisclaimer from '../components/ExportDisclaimer';
import ExportLockedPanel from '../components/ExportLockedPanel';
import ImportQuestion from '../components/ImportQuestion';
import EstimateHeadingPicker from '../components/EstimateHeadingPicker'; // PRP-MCP-15
import type { ExportLockFields } from '../../types/exportSummary';
import { sendExportSummaryEmail, usePreExportDialog } from '../lib/usePreExportDialog';
import { useEstimateChat } from '../lib/useEstimateChat';
import ChatTag from '../components/ChatTag';
import { ChatMarksContext, isChatLine, lineSuffixOf, payloadVersionOf, roomHasChat, useChatMarks } from '../lib/chatChanges';
import { parsedToTreeData } from '../lib/parsedToTreeData';
import { useTreeRevision } from '../lib/priceNoteLines';
import ParkedPriceNotice from '../components/ParkedPriceNotice';
import PriceNotePicker from '../components/PriceNotePicker';
import { collectUnmapped, isUnmappedLine, type UnmappedRef } from '../lib/unmapped';
import { useUiStore } from '../theme/uiStore';

// Lazy-loaded so three.js only enters the bundle when an import/export is
// actually running — keeps the page's initial JS lean (B-292).
const ImportProgress = lazy(() =>
  import('../components/ImportProgress').then((m) => ({ default: m.ImportProgress })),
);

// B-499: lazy so pdf.js (heavy) only loads when the source-PDF viewer is opened.
const PdfSourceViewer = lazy(() => import('../components/PdfSourceViewer'));

/**
 * B-499: the uploaded PDF File for the current import session, exposed to the
 * deeply-nested per-line source preview so it can render the actual page. Null
 * on hydrated/recents paths (no in-memory bytes) → the viewer stays hidden and
 * the B-488 table is shown instead.
 */
const SourcePdfFileContext = createContext<Blob | null>(null);

type ViewMode = 'list' | 'tree' | 'review' | 'enhance';
type Tone = 'ok' | 'amber' | 'bad';

// Adapter for the tree view (exported for tests); lives in ../lib/parsedToTreeData.
export { parsedToTreeData } from '../lib/parsedToTreeData';

// ----------------------------------------------------------------------------
//  Diagnostics tone — mirrors classic classifyDiagnostics (CompletenessComponents)
// ----------------------------------------------------------------------------

/** classify a parser diagnostics payload into ok/amber/bad (classic parity). */
function classifyTone(d: ParserDiagnostics): Tone {
  const lineItems = d.line_items_total ?? d.line_items_emitted ?? 0;
  if (lineItems === 0 || d.confidence < 0.5) return 'bad';
  if (d.confidence < 0.85 || (d.warnings && d.warnings.length > 0)) return 'amber';
  return 'ok';
}

const TONE_BANNER: Record<Tone, 'ok' | 'warn'> = {
  ok: 'ok',
  amber: 'warn',
  bad: 'warn',
};

// ----------------------------------------------------------------------------
//  Helpers
// ----------------------------------------------------------------------------

/** Total line items across all rooms. */
function countLineItems(res: ParsedEstimateResponse): number {
  return res.estimate.rooms.reduce((s, r) => s + r.line_items.length, 0);
}

/** Count of line items whose provenance is MANUAL. */
function countManual(res: ParsedEstimateResponse): number {
  let n = 0;
  for (const room of res.estimate.rooms) {
    for (const li of room.line_items) {
      if (li.provenance === ('MANUAL' as Provenance)) n++;
    }
  }
  return n;
}

const ACCEPT = '.pdf,.xlsx,.xls';

function isValidFile(file: File): boolean {
  return (
    file.type === 'application/pdf' ||
    file.name.toLowerCase().endsWith('.pdf') ||
    file.name.toLowerCase().endsWith('.xlsx') ||
    file.name.toLowerCase().endsWith('.xls')
  );
}

// ----------------------------------------------------------------------------
//  Page
// ----------------------------------------------------------------------------

/** V2 Supplement / import results page. */
export default function V2SupplementPage() {
  const [view, setView] = useState<ViewMode>('tree');
  const [result, setResult] = useState<ParsedEstimateResponse | null>(null);
  // Retain the uploaded File: generateEsxFromPdf needs it for the ESX export.
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // B-600: server-retained source PDF for reopened/background imports — feeds
  // the same context as the in-session upload so the viewer works either way.
  const [fetchedSourcePdf, setFetchedSourcePdf] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Faithful-ESX export state (parity with CompletenessPage.handleDownloadEsx).
  const [esxBusy, setEsxBusy] = useState(false);
  const [esxError, setEsxError] = useState<string | null>(null);
  // Opt-in: include a floor-plan sketch in the ESX. A sketched export ships
  // ManualUpdateAlgorithm:0, so Xactimate opens it calculated/totaled (vs the
  // list-mode export which opens at $0). OFF by default (2026-06-18, founder
  // directive): sketch is opt-in — leave the box unchecked for a plain list-mode
  // export; CHECK it to produce the connected floor-plan sketch (cluster solve).
  // Decision-0017 (2026-07-03): sketch is ADMIN-ONLY until the dims-as-generator
  // rework passes its gate — the toggle is hidden for everyone else and the
  // backend forces sketch off server-side regardless of what a client sends.
  const roles = useAuthStore((s) => s.roles);
  const sketchAllowed = roles.includes('admin') || roles.includes('super_admin');
  const [sketchEnabled, setSketchEnabled] = useState(false);

  // B-400: FEMA/NFIP flood coverage profile toggle. Tri-state:
  //   undefined → user hasn't touched it; the backend auto-detects from the
  //               parsed loss type (a flood claim ships the flood profile).
  //   true/false → explicit override that wins over auto-detect.
  // The SENT value stays undefined until toggled so the server's
  // authoritative loss-type detection isn't overridden.
  const [floodProfile, setFloodProfile] = useState<boolean | undefined>(undefined);

  // Enhance Scope v2 (B-531): the session for the current parse. null = the
  // feature is unavailable (flag off / hash not owned → 404); that null hides
  // the "Enhance" mode entirely. Non-null enables the 4th Segmented option.
  const [enhanceSession, setEnhanceSession] = useState<EnhanceSessionResponse | null>(null);

  // B-7202: set by the pre-download dialog's Download (box unticked); read once
  // runExport has the export's run_ref, so the email names THIS export.
  const emailAfterExportRef = useRef(false);
  // Post-parse project create/match dialog.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addToProjectOpen, setAddToProjectOpen] = useState(false); // PRP-FEEDBACK-2 B-7516a
  const [dialogHeader, setDialogHeader] = useState<EstimateHeaderMetadata | null>(null);
  const [dialogMatch, setDialogMatch] = useState<ProjectMatchResponse | null>(null);

  // B-457: free pre-import confirm dialog (preflight result + chosen options).
  const [preflight, setPreflight] = useState<
    { file: File; pf: PreflightResponse; revising: Project | null } | null
  >(null);
  // B-452: the page's current background import job (watched in importJobsStore).
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  // Remaining files in a multi-file batch — processed one dialog at a time.
  const [fileQueue, setFileQueue] = useState<File[]>([]);
  const jobs = useImportJobsStore((s) => s.jobs);
  // Dedupe the "ready → load" effect so it fires getImport once per job.
  const loadedJobsRef = useRef<Set<string>>(new Set());

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedProject, setSelectedProject, getProjectById } = useProjectStore();

  // --- Supplement flow state machine (B-258) ---
  // Display truth is the ?import= URL param; `result` is its hydration. The
  // page derives: VIEWING (result) / PROJECT_EMPTY (project with no imports) /
  // FRESH (no project, no result) / CHECKING (project switch in flight).
  const [checkingProject, setCheckingProject] = useState(false);
  const [projectHasNoImports, setProjectHasNoImports] = useState(false);
  const [confirmNewSupp, setConfirmNewSupp] = useState(false);
  // Recent imports shown in the FRESH state ("open an existing supplement").
  const [recentImports, setRecentImports] = useState<ImportSummary[] | null>(null);
  const lastReconciledProjectIdRef = useRef<string | null>(null);
  // Set by "Import revised estimate" — the next parse links to the current
  // project and skips the create/match dialog (adjuster back-and-forth).
  const reviseModeRef = useRef(false);

  // Pay-per-conversion (role-less) users: imports are metered. Members never
  // touch the credits surface (unmetered server-side).
  const payPer = useIsPayPerUser();
  const { balance, refresh: refreshCredits } = useCredits(payPer);
  const [creditsOpen, setCreditsOpen] = useState(false);
  // Pre-conversion cost confirmation. When the user hasn't opted out, the
  // styled dialog gates the charged export; its cost drives the copy.
  const exportConfirmEnabled = useUiStore((s) => s.exportCostConfirmEnabled);
  const setExportConfirmEnabled = useUiStore((s) => s.setExportCostConfirmEnabled);
  const [exportCost, setExportCost] = useState<number | null>(null);

  // Sketch was available to everyone while free (Rich, 2026-06-18); superseded
  // by decision-0017 (Rich, 2026-07-03): admin-only until the dim gate is
  // green — see sketchAllowed above.

  const diagnostics = result?.parser_diagnostics ?? result?.parse_summary;

  // Re-open a stored import from the dashboard (?import=<hash>) — free; the
  // charge happened when the import was created. Skipped when the shown
  // result already IS that import (e.g. we just set the param post-upload).
  const importHash = searchParams.get('import');
  useEffect(() => {
    if (!importHash) return;
    if (result?.parse_hash === importHash) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    BackendAPI.getImport(importHash)
      .then((res) => {
        if (!cancelled) setResult(res);
        // B-601: this import is now open — drop its dashboard Processing entry
        // (covers open-from-project and deep links; Processing's own Open
        // button already removes the job before navigating).
        useImportJobsStore.getState().removeJobsByParseHash(importHash);
        // B-600: fetch the retained source PDF so "View in source PDF" works
        // without the in-memory upload. 404 (legacy import, no blob) → the
        // viewer simply stays hidden, as before.
        BackendAPI.getImportSourcePdf(importHash)
          .then((blob) => {
            if (!cancelled) setFetchedSourcePdf(blob);
          })
          .catch(() => {
            if (!cancelled) setFetchedSourcePdf(null);
          });
      })
      .catch((e) => {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : 'Could not load that import.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importHash]);

  // Enhance Scope v2 (B-531): feature-detect on each parse. A 404 (flag off /
  // hash not owned) returns null from the api layer → the "Enhance" mode option
  // never renders. Re-runs when the shown import changes.
  const parseHashForEnhance = result?.parse_hash;
  useEffect(() => {
    if (!parseHashForEnhance) {
      setEnhanceSession(null);
      return;
    }
    let cancelled = false;
    BackendAPI.getEnhanceSession(parseHashForEnhance)
      .then((s) => {
        if (!cancelled) setEnhanceSession(s);
      })
      .catch(() => {
        if (!cancelled) setEnhanceSession(null);
      });
    return () => {
      cancelled = true;
    };
  }, [parseHashForEnhance]);

  // Re-hydrate the session after an apply/revert so applied-proposal state stays
  // correct across mode switches (the panel keeps its own optimistic copy too).
  const reloadEnhanceSession = useCallback(() => {
    if (!parseHashForEnhance) return;
    BackendAPI.getEnhanceSession(parseHashForEnhance)
      .then((s) => setEnhanceSession(s))
      .catch(() => {
        /* leave the current session in place on a transient failure */
      });
  }, [parseHashForEnhance]);

  // If the active view is "enhance" but the feature isn't available for the
  // current import, fall back to the tree so the Segmented value stays valid.
  useEffect(() => {
    if (view === 'enhance' && !enhanceSession) setView('tree');
  }, [view, enhanceSession]);

  // ?new=1 (Dashboard "Import PDF"): start a fresh supplement — clear the
  // project selection + page state and show the import area.
  const newFlag = searchParams.get('new');
  useEffect(() => {
    if (!newFlag) return;
    setSelectedProject(null);
    setResult(null);
    setSelectedFile(null);
    setError(null);
    setProjectHasNoImports(false);
    setRecentImports(null);
    lastReconciledProjectIdRef.current = null;
    setSearchParams(new URLSearchParams(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newFlag]);

  // ?project=<id> consumer: deep links (Dashboard Recents, project detail)
  // also set the top-bar project context, not just the page content.
  const projectParam = searchParams.get('project');
  useEffect(() => {
    if (!projectParam || projectParam === selectedProject?.id) return;
    let cancelled = false;
    void Promise.resolve(getProjectById(projectParam))
      .then((p) => {
        if (!cancelled && p) {
          // Pre-mark as reconciled: the deep link chose the import to show.
          lastReconciledProjectIdRef.current = p.id;
          setSelectedProject(p);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectParam, selectedProject?.id, getProjectById, setSelectedProject]);

  // Project-switch reconciler: when the top-bar switcher lands on a project
  // (and no deep-linked import is pinned), load that project's most recent
  // import — or show the explicit "no imports yet" state. This is what makes
  // switching projects on Supplement actually show that project's supplement.
  useEffect(() => {
    const pid = selectedProject?.id ?? null;
    if (!pid) {
      lastReconciledProjectIdRef.current = null;
      return;
    }
    if (lastReconciledProjectIdRef.current === pid) return;
    if (importHash) {
      // A pinned ?import= deep link wins over auto-loading the newest import.
      lastReconciledProjectIdRef.current = pid;
      return;
    }
    if (loading) return; // a parse is in flight; it will pin its own import
    lastReconciledProjectIdRef.current = pid;
    setCheckingProject(true);
    setProjectHasNoImports(false);
    BackendAPI.listImports(pid)
      .then((rows) => {
        if (lastReconciledProjectIdRef.current !== pid) return; // stale
        const params = new URLSearchParams(searchParams);
        params.set('project', pid);
        if (rows.length > 0) {
          params.set('import', rows[0].parse_hash); // hydrate effect takes over
        } else {
          params.delete('import');
          setResult(null);
          setSelectedFile(null);
          setProjectHasNoImports(true);
        }
        setSearchParams(params, { replace: true });
      })
      .catch(() => {})
      .finally(() => setCheckingProject(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id, importHash, loading]);

  // FRESH state: offer the user's recent imports as "open an existing
  // supplement" (one cheap call; same data as the Dashboard Recents card).
  useEffect(() => {
    if (result || selectedProject || loading || recentImports !== null) return;
    BackendAPI.listImports()
      .then((rows) => setRecentImports(rows))
      .catch(() => setRecentImports([]));
  }, [result, selectedProject, loading, recentImports]);

  const handleDeleteRecentImport = useCallback((imp: ImportSummary) => {
    const label = imp.filename || imp.parse_hash.slice(0, 12);
    if (!window.confirm(`Are you sure you want to permanently delete "${label}"?`)) {
      return;
    }
    // Optimistic removal — drop from the list immediately, restore on failure.
    setRecentImports((prev) =>
      prev ? prev.filter((i) => i.parse_hash !== imp.parse_hash) : prev,
    );
    BackendAPI.deleteImport(imp.parse_hash).catch(() => {
      setRecentImports((prev) => (prev ? [...prev, imp] : prev));
      window.alert('Could not delete that import. Please try again.');
    });
  }, []);

  /**
   * Post-parse hook: match the extracted header against existing projects and
   * surface the create/match dialog. Best-effort — failures fall back to plain
   * parse output. Ported from CompletenessPage.runProjectMatch.
   */
  const runProjectMatch = useCallback(
    async (header?: EstimateHeaderMetadata | null) => {
      if (!header) return;
      const hasSignal = header.claim_number || header.owner_name || header.address?.street;
      if (!hasSignal) return;

      let match: ProjectMatchResponse | null = null;
      try {
        match = await BackendAPI.matchProject(header);
      } catch (err) {
        // Best-effort: still show the dialog (without suggestions) on failure.
        console.warn('[v2-supplement] project match failed:', err);
        match = { exact_match: null, fuzzy_matches: [] };
      }
      // Obvious-answer shortcut: the selected project already IS the exact match.
      if (selectedProject && match.exact_match && match.exact_match.id === selectedProject.id) {
        return;
      }
      setDialogHeader(header);
      setDialogMatch(match);
      setDialogOpen(true);
    },
    [selectedProject],
  );

  /** Gate the metered import: a pay-per user with 0 credits gets the
   *  buy/redeem modal instead of the file picker. (The 402 handler below
   *  covers stale-balance races.) */
  const guardImport = useCallback((): boolean => {
    if (payPer && balance !== null && balance <= 0) {
      setCreditsOpen(true);
      return false;
    }
    return true;
  }, [payPer, balance]);

  // B-457: drop a file → cheap FREE preflight → open the confirm dialog. The
  // (free) background parse is only enqueued once the user clicks Proceed.
  const handleFile = useCallback(
    async (file: File) => {
      if (!isValidFile(file)) {
        setError('Unsupported file. Please upload a PDF or Excel (.xlsx) estimate.');
        return;
      }
      if (!guardImport()) return;
      setSelectedFile(file);
      setLoading(true);
      setError(null);
      try {
        const revising = reviseModeRef.current && selectedProject ? selectedProject : null;
        reviseModeRef.current = false;
        const pf = await BackendAPI.preflightEstimate(file);
        setPreflight({ file, pf, revising });
      } catch (e) {
        const apiErr = e as ApiError;
        if (apiErr.status === 402) {
          // Out of credits server-side: open the buy/redeem flow.
          setCreditsOpen(true);
        } else {
          setError(
            e instanceof Error ? e.message : 'Failed to read the estimate. Please try again.',
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [guardImport, selectedProject],
  );

  /** Pop the next file in a multi-file batch (each becomes its own job/card). */
  const advanceQueue = useCallback(() => {
    setFileQueue((q) => {
      if (q.length === 0) return q;
      const [next, ...rest] = q;
      void handleFile(next);
      return rest;
    });
  }, [handleFile]);

  // Proceed from the pre-import dialog: enqueue the FREE background parse, register
  // it in the store (so it survives navigation), and show ImportProgress.
  const onPreImportProceed = useCallback(
    async (decision: PreImportDecision) => {
      if (!preflight) return;
      const { file, revising, pf } = preflight;
      setPreflight(null);
      setLoading(true);
      // Sketch is generated at EXPORT, not at parse. The dialog toggle must
      // therefore drive the page's export sketch preference (the same state the
      // export "generate sketch" checkbox uses) so export honors the choice.
      if (decision.enableSketch && sketchAllowed) setSketchEnabled(true);
      let projectId = revising?.id ?? decision.projectId;
      // "Create new project" from the preflight dialog: actually create it from
      // the extracted header BEFORE enqueueing, then link the import to it (via
      // both projectId on enqueue and project_id on the job, so the foreground
      // ready-path AND the background queue worker's set_project_link agree).
      // Without this the create-new choice silently did nothing (live bug).
      if (!projectId && decision.createNew) {
        const created = await useProjectStore
          .getState()
          .createProject(preflightHeaderToNewProjectForm(pf.header_metadata));
        if (!created) {
          // Don't silently enqueue an unlinked import — surface the failure and
          // stop so the user can retry rather than ending up with an orphan.
          setLoading(false);
          setError('Could not create the new project. Please try again.');
          return;
        }
        projectId = created.id;
      }
      try {
        const enq = await BackendAPI.enqueueParse(file, {
          projectId,
          enableSketch: decision.enableSketch,
        });
        useImportJobsStore.getState().addJob({
          job_id: enq.job_id,
          filename: file.name,
          status: 'queued',
          started_at: Date.now(),
          parse_hash: enq.parse_hash,
          project_id: projectId ?? null,
        });
        useImportJobsStore.getState().startPolling();
        setActiveJobId(enq.job_id);
        if (payPer) void refreshCredits().catch(() => {});
      } catch (e) {
        setLoading(false);
        const apiErr = e as ApiError;
        if (apiErr.status === 402) setCreditsOpen(true);
        else setError(e instanceof Error ? e.message : 'Failed to start the import.');
      }
    },
    [preflight, payPer, refreshCredits],
  );

  /** Cancel the pre-import dialog (abandons the rest of a multi-file batch). */
  const onPreImportCancel = useCallback(() => {
    setPreflight(null);
    setFileQueue([]);
    setLoading(false);
  }, []);

  // B-452: watch the page's active background job. On ready → load via the
  // existing GET /imports/{hash} render path; on failed → surface the error.
  useEffect(() => {
    if (!activeJobId) return;
    const job = jobs.find((j) => j.job_id === activeJobId);
    if (!job) return;
    if (job.status === 'ready') {
      // Dedupe: the effect re-runs on every poll tick (jobs array changes), so
      // load each ready job exactly once and let the promise run to completion
      // (no per-run cancel — a cleanup would abort the in-flight setResult).
      if (loadedJobsRef.current.has(job.job_id)) return;
      loadedJobsRef.current.add(job.job_id);
      setActiveJobId(null);
      setLoading(true);
      BackendAPI.getImport(job.parse_hash)
        .then((res) => {
          setResult(res);
          setProjectHasNoImports(false);
          // B-601: the import is open right here — it must not keep sitting in
          // the dashboard Processing list with an Open button.
          useImportJobsStore.getState().removeJobsByParseHash(job.parse_hash);
          // Sync the deep-link now that the import is loaded; the re-hydration
          // effect's guard (result.parse_hash === importHash) skips a refetch.
          const params = new URLSearchParams(searchParams);
          params.set('import', job.parse_hash);
          if (job.project_id) params.set('project', job.project_id);
          setSearchParams(params, { replace: true });
          if (job.project_id) {
            lastReconciledProjectIdRef.current = job.project_id;
            void BackendAPI.linkImportToProject(job.parse_hash, job.project_id).catch(() => {
              setError(
                'Imported, but linking it to the project failed — use the project dialog to relink.',
              );
            });
          } else {
            void runProjectMatch(res.header_metadata ?? null);
          }
          advanceQueue();
        })
        .catch(() => setError('Import finished but could not be loaded.'))
        .finally(() => setLoading(false));
    }
    if (job.status === 'failed') {
      setActiveJobId(null);
      setLoading(false);
      setError(job.error || 'Import failed. Please try again.');
    }
  }, [activeJobId, jobs, runProjectMatch, advanceQueue, searchParams, setSearchParams]);

  // Export the faithful import as an importable Xactimate .ESX. Reuses the
  // proven PDF→ESX path: the backend parses the file, matches catalog codes,
  // and returns the SCOPE as ESX (not the raw PDF).
  // The actual charged export — runs after the cost is confirmed (or when no
  // confirmation is needed: free, member, or "don't ask again" opted out).
  // PRP-MCP-8: version 'original' exports an import's Original (the locked panel offers it).
  // PRP-MCP-8 item 10: the export the import question follows (shown after a successful export).
  const [lastExport, setLastExport] = useState<{ parseHash: string; runRef?: string; at?: string } | null>(null);
  const runExport = useCallback(async (version?: 'original') => {
    const parseHash = result?.parse_hash;
    const cost = parseHash ? result?.credits_required : undefined;
    setEsxBusy(true);
    setEsxError(null);
    try {
      // W1 convergence: export the PERSISTED parse the user just reviewed
      // (WYSIWYG) via its parse_hash, so the download matches the display
      // exactly. Fall back to the legacy re-upload path only if no parse_hash
      // is available (e.g. an older parse with no hash in state). `cost` echoes
      // the price the user confirmed → backend 409s if it has since changed.
      const res = parseHash
        ? await BackendAPI.generateEsxFromParse(
            parseHash, undefined, sketchEnabled, cost, floodProfile, ...(version ? [version] as const : []),
          )
        : await BackendAPI.generateEsxFromPdf(selectedFile as File);
      triggerEsxDownload(res.filename, res.esx_b64);
      if (parseHash) setLastExport({ parseHash, runRef: res.run_ref, at: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }); // B-7496
      // B-7202: the summary email for THIS export (pre-download dialog, box unticked).
      if (emailAfterExportRef.current && parseHash && res.run_ref && !version) {
        sendExportSummaryEmail(parseHash, res.run_ref);
      }
      emailAfterExportRef.current = false;
      // The export is the charged step — refresh the balance chip now.
      if (payPer) void refreshCredits().catch(() => {});
    } catch (err) {
      const apiErr = err as ApiError & { code?: string };
      if (apiErr.status === 402 && apiErr.code === 'export_locked' && parseHash) {
        // PRP-MCP-8: chat work locked since the page loaded; re-read the lock, show the panel.
        void BackendAPI.getImport(parseHash)
          .then((r) => setResult((prev) => prev && { ...prev, export_lock: (r as ExportLockFields).export_lock }))
          .catch(() => {});
      } else if (apiErr.status === 402) {
        setCreditsOpen(true); // out of credits at charge time (stale-balance race)
      } else if (apiErr.status === 409) {
        setEsxError(
          'The price changed since this estimate was parsed — re-import it to see the updated cost.',
        );
      } else {
        setEsxError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setEsxBusy(false);
    }
  }, [selectedFile, result, sketchEnabled, floodProfile, payPer, refreshCredits]);

  // Entry point for the Download ESX button. Decides whether to charge-confirm:
  //   - free export (cost 0) or member → export straight away
  //   - balance known to be short → short-circuit to the buy/redeem flow
  //   - cost > 0 + confirmation enabled → open the styled cost dialog
  //   - cost > 0 + "don't ask again" → export straight away
  // The legacy re-upload fallback (no parse_hash) charges a flat credit
  // server-side and ignores confirm_credits, so it's never price-confirmed here.
  const handleDownloadEsx = useCallback(() => {
    if (!selectedFile && !result) return;
    const parseHash = result?.parse_hash;
    const cost = parseHash ? (result?.credits_required ?? 0) : 0;
    if (payPer && cost > 0) {
      // Only short-circuit to buy/redeem when we KNOW the balance is short;
      // while it's still loading (null) let the backend 402 be the real gate.
      if (balance !== null && balance < cost) {
        setCreditsOpen(true);
        return;
      }
      if (exportConfirmEnabled) {
        setExportCost(cost);
        return;
      }
    }
    void runExport();
  }, [selectedFile, result, payPer, balance, exportConfirmEnabled, runExport]);

  // B-7202: the Download ESX button opens the pre-download review dialog.
  const preExportDialog = usePreExportDialog({
    parseHash: result?.parse_hash,
    download: handleDownloadEsx,
    openReview: () => setView('review'),
    emailAfterExportRef,
  });

  // PRP-MCP-5: chat edits land on this estimate; tags, change log, polling.
  const estimateChat = useEstimateChat(result, setResult);
  const exportLock = (result as ExportLockFields | null)?.export_lock ?? null; // PRP-MCP-8

  // Confirm from the cost dialog: persist the "don't ask again" choice, then export.
  const handleConfirmExport = useCallback(
    (dontAskAgain: boolean) => {
      if (dontAskAgain) setExportConfirmEnabled(false);
      setExportCost(null);
      void runExport();
    },
    [runExport, setExportConfirmEnabled],
  );

  // Associate the parse with a project (create or match): pin it in the URL
  // AND persist the link server-side so the import re-opens from the
  // dashboard after logout/redeploy (it used to live only in the URL param).
  const associateProject = useCallback(
    (project: Project) => {
      setDialogOpen(false);
      setAddToProjectOpen(false);
      // Pre-mark reconciled: the import on screen IS this project's supplement.
      lastReconciledProjectIdRef.current = project.id;
      setSelectedProject(project);
      const params = new URLSearchParams(searchParams);
      params.set('project', project.id);
      setSearchParams(params);
      // Fall back to the URL param — `result` can lag the dialog briefly.
      const parseHash = result?.parse_hash ?? searchParams.get('import');
      if (parseHash) {
        void BackendAPI.linkImportToProject(parseHash, project.id).then(() => {
          // B-7516a: the page knows the import is linked only once the server saved the link.
          setResult((prev) => (prev && prev.parse_hash === parseHash ? { ...prev, project_id: project.id } : prev));
        }).catch((err) => {
          // Surface it: an unlinked import is invisible from the project later
          // (this exact silent failure orphaned the founder's demo imports).
          console.warn('[v2-supplement] import->project link failed:', err);
          setError(
            'Project selected, but linking the import to it failed — re-open the import from Recents and pick the project again.',
          );
        });
      }
    },
    [setSelectedProject, searchParams, setSearchParams, result],
  );

  /** Clears everything and shows the fresh import area ("New Supplement"). */
  const startNewSupplement = useCallback(() => {
    setConfirmNewSupp(false);
    setSelectedProject(null);
    setResult(null);
    setSelectedFile(null);
    setError(null);
    setEsxError(null);
    setProjectHasNoImports(false);
    setRecentImports(null);
    lastReconciledProjectIdRef.current = null;
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSelectedProject, setSearchParams]);

  /** Imports a revised estimate into the CURRENT project (no new project). */
  const handleReviseImport = useCallback(() => {
    if (!guardImport()) return;
    reviseModeRef.current = true;
    fileInputRef.current?.click();
  }, [guardImport]);

  const handleDialogClose = useCallback(() => setDialogOpen(false), []);

  /** Start a (possibly multi-file) batch: preflight the first, queue the rest. */
  const startBatch = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const [first, ...rest] = files;
      setFileQueue(rest);
      void handleFile(first);
    },
    [handleFile],
  );

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    startBatch(Array.from(e.dataTransfer.files));
  };

  const triggerBrowse = () => {
    if (!guardImport()) return;
    fileInputRef.current?.click();
  };

  return (
    <div
      className="px-6 py-5 mx-auto w-full"
      style={{ maxWidth: view === 'tree' ? 'none' : 1080 }}
    >
      <EstimateChangedNotice />
      {result && <FieldProjectBanner estimate={result.estimate} />}
      {/* page head */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-[20px] font-[560] text-[var(--tx-hi)] tracking-[-0.01em]">
            Supplement
          </h1>
          <p className="text-[13px] text-[var(--tx-lo)] mt-1">
            The estimate by room and heading, exactly as imported or built. Download as ESX.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 md:flex-none md:max-w-full">
          {payPer && (
            <button
              type="button"
              onClick={() => setCreditsOpen(true)}
              title="Import credits — each credit converts one estimate PDF. Click to buy or redeem a code."
              className="flex h-8 items-center gap-1.5 rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-2)] px-2.5 text-[12px] text-[var(--tx-mid)] hover:border-[var(--bd-strong)]"
              data-testid="credits-chip"
            >
              <span className="font-[var(--font-mono)] font-semibold text-[var(--tx-hi)]">
                {balance ?? '…'}
              </span>
              {balance === 1 ? 'credit' : 'credits'}
            </button>
          )}
          <Segmented<ViewMode>
            value={view}
            onValueChange={setView}
            options={[
              { value: 'list', label: 'List', icon: <IconList /> },
              { value: 'tree', label: 'Tree', icon: <IconFolder /> },
              {
                value: 'review',
                // B-437b: surface the unmapped count right on the tab so the
                // estimator can see + fix them before export.
                label: result && result.match_stats.unmatched > 0
                  ? `Review (${result.match_stats.unmatched})`
                  : 'Review',
                icon: <IconList />,
              },
              // B-531: the "Enhance" mode only appears when the feature is
              // available for this parse (getEnhanceSession returned non-null).
              ...(enhanceSession
                ? [{ value: 'enhance' as const, label: 'Enhance', icon: <IconSpark /> }]
                : ([] as SegmentedOption<ViewMode>[])),
            ]}
          />
          {(result || selectedProject) && (
            <Button
              size="sm"
              onClick={() => setConfirmNewSupp(true)}
              title="Save this supplement and start a new one for another project"
            >
              <IconUpload />
              New Supplement
            </Button>
          )}
          {sketchAllowed && (
            <label
              className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] select-none cursor-pointer"
              title="Include a floor-plan sketch (free, admin-only while in rework). Sketched exports open calculated/totaled in Xactimate. Experimental: multi-room arrangement is still being refined."
            >
              <input
                type="checkbox"
                checked={sketchEnabled}
                onChange={(e) => setSketchEnabled(e.target.checked)}
                className="accent-[var(--accent)]"
              />
              Sketch
            </label>
          )}
          <label
            className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] select-none cursor-pointer"
            title="Export in the FEMA/NFIP flood coverage profile (Building, ICC, Debris Removal, …) so it imports already in the flood profile — no manual 'clone to flood' in Xactimate. Defaults on for flood claims. Does not change unit prices."
          >
            <input
              type="checkbox"
              checked={floodProfile ?? false}
              onChange={(e) => setFloodProfile(e.target.checked)}
              className="accent-[var(--accent)]"
            />
            FEMA Flood
          </label>
          {payPer && result && !exportLock && (
            <span
              className="text-[11px] font-medium"
              style={{ color: (result.credits_required ?? 0) > 0 ? 'var(--accent)' : 'var(--ok)' }}
              data-testid="export-cost"
            >
              {(result.credits_required ?? 0) > 0
                ? `Export: ${result.credits_required} credit${result.credits_required === 1 ? '' : 's'}`
                : 'Export: free'}
            </span>
          )}
          {exportLock && result ? (
            <ExportLockedPanel
              lock={exportLock}
              parseHash={result.parse_hash}
              onExportOriginal={canShowOriginal(result) ? () => void runExport('original') : undefined}
              busy={esxBusy}
            />
          ) : (
            <Button
              size="sm"
              variant="primary"
              onClick={() => void preExportDialog.openPreExport()}
              disabled={esxBusy || !result}
              title="Export the faithful import as an importable Xactimate .ESX"
            >
              <IconDownload />
              {esxBusy ? 'Exporting…' : 'Download ESX'}
            </Button>
          )}
          {result && <div className="basis-full"><EstimateHeadingPicker parseHash={result.parse_hash} /></div>}
          <ExportPriceListNote className="basis-full text-right" />
          <ExportDisclaimer className="basis-full text-right" />
          {lastExport && lastExport.parseHash === result?.parse_hash && (
            <div className="basis-full">
              {lastExport.at && <p className="text-right text-xs text-[var(--text-secondary)]">Last exported {lastExport.at}</p>}
              <ImportQuestion parseHash={lastExport.parseHash} exportRef={lastExport.runRef} />
            </div>
          )}
        </div>
      </div>

      {/* Hidden file input stays mounted even when the dropzone is hidden so
          "Import revised estimate" can open the picker from the result view. */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        data-testid="supplement-file-input"
        onChange={(e) => {
          startBatch(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />

      {/* compact dropzone — only while there's nothing imported on screen */}
      {!result && !loading && (
      <div
        role="button"
        tabIndex={0}
        aria-label="Upload an estimate file"
        onClick={triggerBrowse}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            triggerBrowse();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={cn(
          'mb-[14px] flex items-center gap-4 text-left cursor-pointer rounded-[var(--r-lg)] p-6',
          'border-2 border-dashed bg-[var(--bg-1)] transition-colors outline-none',
          dragOver
            ? 'border-[var(--accent-line)] bg-[var(--accent-soft)]'
            : 'border-[var(--bd)] hover:border-[var(--bd-strong)]',
          'focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]',
          loading && 'pointer-events-none opacity-60',
        )}
      >
        <div className="grid place-items-center w-[42px] h-[42px] rounded-[10px] bg-[var(--bg-3)] border border-[var(--bd-subtle)] text-[var(--tx-lo)] flex-none [&_svg]:w-5 [&_svg]:h-5">
          <IconUpload />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-[560] text-[var(--tx-hi)] text-[13px]">
            Drag and drop an estimate here, or click to browse
          </div>
          <div className="text-[11.5px] text-[var(--tx-lo)] mt-0.5">
            Supports PDF and Excel (.xlsx). Importing shows a faithful reproduction first —
            enhancements are opt-in.
          </div>
        </div>
      </div>
      )}

      {/* error */}
      {error && (
        <Banner tone="warn" icon={<IconWarn />} className="mb-[14px]">
          {error}
        </Banner>
      )}
      {esxError && (
        <Banner tone="warn" icon={<IconWarn />} className="mb-[14px]">
          ESX export failed: {esxError}
        </Banner>
      )}

      {/* loading — creative Three.js import experience (B-292) replaces the
          old 4-blank-skeleton "blind spinner". */}
      {loading && (
        <Suspense fallback={<Skeleton className="h-[360px] w-full" />}>
          <ImportProgress
            variant="import"
            // Only offer "Run in background" once a job is actually enqueued
            // (it keeps polling in importJobsStore after navigation).
            onRunInBackground={activeJobId ? () => navigate('/') : undefined}
          />
        </Suspense>
      )}

      {/* project switch in flight */}
      {!loading && !result && checkingProject && <Skeleton className="h-[120px] w-full" />}

      {/* empty — branch on whether a project is selected (PROJECT_EMPTY vs FRESH) */}
      {!loading && !result && !checkingProject && (
        <>
          <Card className="p-0">
            <EmptyState
              icon={<IconUpload />}
              title={
                selectedProject && projectHasNoImports
                  ? 'No estimate imported for this project yet'
                  : 'No estimate imported yet'
              }
              description={
                selectedProject && projectHasNoImports
                  ? "Drop the carrier estimate above to start this project's supplement."
                  : 'Drop a PDF or Excel estimate above (or click to browse) to see a faithful import.'
              }
              action={
                <Button variant="primary" onClick={triggerBrowse}>
                  <IconUpload />
                  Choose a file
                </Button>
              }
            />
          </Card>

          {/* FRESH state: open an existing supplement instead */}
          {!selectedProject && recentImports && recentImports.length > 0 && (
            <Card className="mt-3 px-4 py-[14px]">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-[.06em] text-[var(--tx-faint)]">
                Or open an existing supplement
              </div>
              <div className="flex flex-col">
                {recentImports.slice(0, 8).map((imp) => (
                  <div
                    key={imp.parse_hash}
                    className="flex items-center gap-2 rounded-[var(--r-sm)] px-2 py-[7px] hover:bg-[var(--bg-3)]"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        const params = new URLSearchParams();
                        params.set('import', imp.parse_hash);
                        if (imp.project_id) params.set('project', imp.project_id);
                        setSearchParams(params);
                      }}
                      className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
                    >
                      <span className="min-w-0 truncate text-[12.5px] text-[var(--tx-hi)]">
                        {imp.filename || imp.parse_hash.slice(0, 12)}
                      </span>
                      <span className="flex-none text-[11px] text-[var(--tx-lo)]">
                        {imp.created_at ? new Date(imp.created_at).toLocaleDateString() : ''}
                      </span>
                    </button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteRecentImport(imp)}
                      aria-label={`Delete ${imp.filename || 'import'}`}
                    >
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* result */}
      {!loading && result && (
        <SourcePdfFileContext.Provider value={selectedFile ?? fetchedSourcePdf}>
        <ChatMarksContext.Provider value={estimateChat.marks}>
        <EstimateTabs
          estimateId={result.parse_hash}
          buildList={estimateChat.panel}
          onEstimateChanged={estimateChat.reload}
          version={payloadVersionOf(result)}
          resetKey={view}
        >
        <Result
          result={result}
          diagnostics={diagnostics}
          view={view}
          onSetView={setView}
          reviewFlagged={preExportDialog.reviewFlagged}
          onReviseImport={handleReviseImport}
          onResult={setResult}
          linkedProjectId={result.project_id ?? null} // B-7516a: the stored link only; a failed link keeps the control
          onAddToProject={() => setAddToProjectOpen(true)}
          enhanceSession={enhanceSession}
          onEnhanceSessionChanged={reloadEnhanceSession}
        />
        </EstimateTabs>
        </ChatMarksContext.Provider>
        </SourcePdfFileContext.Provider>
      )}

      {/* New Supplement confirmation */}
      {confirmNewSupp && (
        <Modal
          label="Start a new supplement"
          title="Start a new Supplement Project?"
          subtitle="Your current supplement is saved automatically and stays available from the Dashboard and its project."
          onClose={() => setConfirmNewSupp(false)}
          testId="new-supplement-confirm"
          className="max-w-md"
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmNewSupp(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={startNewSupplement}>
                Continue
              </Button>
            </>
          }
        >
          <span className="text-[12.5px] text-[var(--tx-mid)]">
            This clears the project selection and opens a fresh import screen.
          </span>
        </Modal>
      )}

      {/* pre-import confirm dialog (free preflight) */}
      {preflight && (
        <PreImportDialog
          preflight={preflight.pf}
          filename={preflight.file.name}
          onProceed={onPreImportProceed}
          onCancel={onPreImportCancel}
        />
      )}

      {/* PRP-FEEDBACK-2 B-7516a: attach an import with no project, after the fact */}
      {addToProjectOpen && (
        <AddToProjectDialog
          header={result?.header_metadata ?? null}
          onPick={associateProject}
          onClose={() => setAddToProjectOpen(false)}
        />
      )}

      {/* post-parse project create/match dialog */}
      {dialogOpen && dialogHeader && (
        <PostParseProjectDialog
          header={dialogHeader}
          match={dialogMatch}
          currentProject={selectedProject ?? null}
          onUseProject={associateProject}
          onProjectCreated={associateProject}
          onClose={handleDialogClose}
        />
      )}

      {/* pre-conversion cost confirmation (replaces window.confirm) */}
      {exportCost !== null && (
        <ExportCostDialog
          cost={exportCost}
          balance={balance}
          onConfirm={handleConfirmExport}
          onCancel={() => setExportCost(null)}
        />
      )}

      {/* B-7202: pre-download review dialog — review callout, steps, lines */}
      {preExportDialog.dialog}

      {/* out-of-credits: buy / redeem / earn-via-feedback */}
      {creditsOpen && (
        <GetCreditsModal
          onClose={() => setCreditsOpen(false)}
          onRedeemed={() => {
            void refreshCredits().catch(() => {});
          }}
        />
      )}

      {/* ESX export progress (B-292) — a non-dismissable overlay so the user
          can see the (blocking) generate is alive. Same creative experience as
          import, with export copy + a lighter scene. */}
      {esxBusy && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Generating your ESX"
          data-testid="esx-progress-overlay"
        >
          <Suspense fallback={<Skeleton className="h-[360px] w-full max-w-[420px]" />}>
            <ImportProgress
              variant="export"
              itemCount={result ? countLineItems(result) : undefined}
              className="w-full max-w-[420px] shadow-[var(--sh-lg)]"
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
//  Result body
// ----------------------------------------------------------------------------

interface ResultProps {
  result: ParsedEstimateResponse;
  diagnostics: ParserDiagnostics | undefined;
  view: ViewMode;
  /** Lets child controls (the clickable Unmapped stat) switch the active view. */
  onSetView: (_v: ViewMode) => void;
  /** Low-prominence path for importing a REVISED estimate into the same project. */
  onReviseImport: () => void;
  onResult: (_res: ParsedEstimateResponse) => void;
  /** B-531: the enhance session for this parse (null → mode unavailable). */
  enhanceSession: EnhanceSessionResponse | null;
  /** B-531: re-hydrate the session after an apply/revert. */
  onEnhanceSessionChanged: () => void;
  /** B-7202: source line numbers the export summary flagged (shown in Review). */
  reviewFlagged?: Set<number>;
  /** PRP-FEEDBACK-2 B-7516a: the project this import is in, or null (shows Add to project). */
  linkedProjectId?: string | null;
  onAddToProject?: () => void;
}

function Result({
  result,
  diagnostics,
  view,
  onSetView,
  onReviseImport,
  onResult,
  enhanceSession,
  onEnhanceSessionChanged,
  reviewFlagged,
  linkedProjectId = null,
  onAddToProject,
}: ResultProps) {
  const meta = result.estimate.metadata;
  const header = result.header_metadata;
  const lineItemCount = useMemo(() => countLineItems(result), [result]);
  const manualCount = useMemo(() => countManual(result), [result]);
  const roomCount = result.estimate.rooms.length;
  const { matched, unmatched } = result.match_stats;
  const chatMarks = useChatMarks();
  const originalView = useOriginalView(result, view); // G9: any view change returns to Enhanced
  const treeData = useMemo(() => parsedToTreeData(result, chatMarks), [result, chatMarks]);
  // The tree keeps its rows in local state: remount it when the estimate or its chat marks move.
  const { treeRevision, onLineResult } = useTreeRevision(result, onResult);
  const treeKey = `${treeRevision ?? ''}|${chatMarks.onlyChat}|${[...chatMarks.index.lines, ...chatMarks.index.rooms].join(',')}`;

  const address =
    meta.property_address ||
    (header?.address
      ? [header.address.street, header.address.city, header.address.state, header.address.zip]
          .filter(Boolean)
          .join(', ')
      : null) ||
    '—';

  return (
    <>
      {diagnostics && <DiagnosticsBanner d={diagnostics} />}
      <ParkedPriceNotice result={result} />

      {/* estimate meta grid */}
      <Card className="px-4 py-[14px] mb-3">
        <div className="flex flex-wrap items-center gap-[10px] mb-3">
          {/* PRP-FEEDBACK-2 B-7515: the display name, renamed inline. */}
          {result.parse_hash ? (
            <ImportNameField
              parseHash={result.parse_hash}
              name={result.name ?? meta.estimate_name}
              onRenamed={(name) =>
                onResult({ ...result, name, estimate: { ...result.estimate, metadata: { ...meta, estimate_name: name } } })
              }
            />
          ) : (
            <span className="font-[var(--font-mono)] text-[13px] font-semibold text-[var(--tx-hi)]">
              {meta.estimate_name || 'Imported estimate'}
            </span>
          )}
          {/* PRP-FEEDBACK-2 B-7516a: an import in no project can be filed under one here. */}
          {result.parse_hash && !linkedProjectId && onAddToProject && (
            <Button size="sm" variant="secondary" type="button" onClick={onAddToProject} data-testid="add-to-project">
              Add to project
            </Button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-x-6 gap-y-[10px] text-[12.5px]">
          {(
            [
              ['Insured', meta.insured_name || header?.owner_name || '—'],
              ['Property address', address],
              ['Type of loss', meta.damage_type || header?.loss_type || '—'],
              ['Date of loss', meta.date_of_loss || '—'],
              ['Price list', meta.price_list || '—'],
              ['Estimate id', result.parse_hash || meta.estimate_name || '—'],
            ] as [string, string][]
          ).map(([k, v]) => (
            <div key={k}>
              <div className="text-[10.5px] text-[var(--tx-lo)] mb-0.5">{k}</div>
              <div className="text-[var(--tx-hi)] truncate" title={v}>
                {v}
              </div>
            </div>
          ))}
          {/* PRP-FEEDBACK-1 W1 (R4): the O&P percentages the export writes, editable here. */}
          {result.parse_hash && <OpRatesField parseHash={result.parse_hash} />}
        </div>
      </Card>
      {/* PRP-ROOF-7 W3 (D9): the vendor-measured roof attached to this estimate. */}
      {result.parse_hash && <RoofAttachCard parseHash={result.parse_hash} />}
      {/* PRP-NOTES-1: chat's suggested customer notes, reviewed here; the estimate's note level. */}
      {result.parse_hash && <NoteReviewPanel parseHash={result.parse_hash} />}

      {/* stat cards */}
      <div className="flex gap-3 mb-[14px]">
        <Stat k="Rooms" v={roomCount} />
        <Stat k="Line items" v={lineItemCount} />
        <Stat k="Mapped" v={matched} tone="ok" />
        <Stat
          k="Unmapped"
          v={unmatched}
          tone={unmatched > 0 ? 'bad' : undefined}
          // B-437b: the unmapped count is the entry point to the review list.
          onClick={unmatched > 0 ? () => onSetView('review') : undefined}
        />
        <Stat k="Manual" v={manualCount} />
      </div>

      {/* PRP-INTAKE-1 (I3): the Faithful import bar, Suggest enhancements and the
          claim-type select are gone; the import and Enhance actions stay. */}
      <div className="flex justify-end items-center flex-wrap gap-2 mb-[14px]">
        <Button
          size="sm"
          variant="ghost"
          onClick={onReviseImport}
          title="Got a revised estimate from the adjuster? Import it into this same project — the current import stays in history."
        >
          <IconRefresh />
          Import revised estimate…
        </Button>
        {enhanceSession && (
          <Button
            size="sm"
            variant="primary"
            onClick={() => onSetView('enhance')}
            title="Open the interactive Enhance workspace to propose, review, and apply supplement items."
          >
            <IconSpark />
            Enhance…
          </Button>
        )}
      </div>

      {/* rooms — enhance (B-531), tree, list, or the B-437b review list; G9 Original | Enhanced */}
      <OriginalOrEnhanced ov={originalView}>
      {view === 'enhance' && enhanceSession ? (
        <EnhancePanel
          result={result}
          parseHash={result.parse_hash}
          session={enhanceSession}
          onResult={onResult}
          onSessionChanged={onEnhanceSessionChanged}
        />
      ) : view === 'review' ? (
        <ReviewView result={result} onResult={onResult} flagged={reviewFlagged} />
      ) : view === 'tree' ? (
        <Card className="h-[600px] overflow-hidden p-0">
          <ScopeTreeTable
            key={treeKey}
            data={treeData}
            lineAction={(it) => (
              <PriceNotePicker
                parseHash={result.parse_hash}
                lines={[{ room_index: it.roomIdx!, line_item_index: it.itemIdx! }]}
                current={it.priceNote}
                code={`${it.cat} ${it.sel}`}
                description={it.desc}
                onResult={onLineResult}
              />
            )}
          />
        </Card>
      ) : (
        <ListView result={result} onResult={onLineResult} />
      )}
      </OriginalOrEnhanced>
    </>
  );
}

// ----------------------------------------------------------------------------
//  Diagnostics banner
// ----------------------------------------------------------------------------

function DiagnosticsBanner({ d }: { d: ParserDiagnostics }) {
  const tone = classifyTone(d);
  const confidencePct = Math.round((d.confidence ?? 0) * 100);
  const lineItems = d.line_items_total ?? d.line_items_emitted ?? 0;
  const preamble = d.pages_skipped_as_preamble ?? 0;

  const bars: [string, number][] = [
    ['CAT', d.pct_cat_populated ?? 0],
    ['SEL', d.pct_sel_populated ?? 0],
    ['Activity', d.pct_activity_populated ?? 0],
    ['Unit', d.pct_unit_populated ?? 0],
  ];

  return (
    <Banner
      tone={TONE_BANNER[tone]}
      icon={tone === 'ok' ? <IconCheck /> : <IconWarn />}
      className="mb-[14px]"
    >
      <div className="text-[12.5px] text-[var(--tx-hi)]">
        Parsed via <b className="font-[var(--font-mono)]">{d.strategy || 'unknown'}</b> · confidence{' '}
        <b>{confidencePct}%</b> · {lineItems} line items from {d.pages_with_line_items ?? 0} of{' '}
        {d.pages_total ?? 0} pages
        {preamble > 0 && (
          <>
            {' '}
            · {preamble} {preamble === 1 ? 'page' : 'pages'} skipped as preamble
          </>
        )}
      </div>
      <div className="flex gap-[18px] mt-2 flex-wrap">
        {bars.map(([label, v]) => (
          <div key={label} className="flex items-center gap-[7px]">
            <span className="text-[11px] text-[var(--tx-lo)] w-[52px]">{label}</span>
            <div className="w-20 h-[5px] rounded-full bg-[var(--bg-4)] overflow-hidden">
              <div
                className="h-full"
                style={{
                  width: `${Math.max(0, Math.min(100, v))}%`,
                  background: v >= 100 ? 'var(--ok)' : 'var(--warn)',
                }}
              />
            </div>
            <span className="font-[var(--font-mono)] text-[11px] text-[var(--tx-mid)] w-[30px]">
              {Math.round(v)}%
            </span>
          </div>
        ))}
      </div>
      {d.warnings && d.warnings.length > 0 && (
        <ul className="list-disc pl-5 mt-2 text-[11px] text-[var(--tx-mid)] space-y-0.5">
          {d.warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
    </Banner>
  );
}

// ----------------------------------------------------------------------------
//  Stat card
// ----------------------------------------------------------------------------

function Stat({
  k,
  v,
  tone,
  onClick,
}: {
  k: string;
  v: number;
  tone?: 'ok' | 'bad';
  onClick?: () => void;
}) {
  const body = (
    <>
      <div className="text-[10.5px] uppercase tracking-[.05em] text-[var(--tx-lo)] mb-1">
        {k}
        {onClick && <span className="ml-1 text-[var(--accent-ink)] normal-case">· review →</span>}
      </div>
      <div
        className="text-[22px] font-[600] font-[var(--font-mono)]"
        style={{ color: tone ? `var(--${tone})` : 'var(--tx-hi)' }}
      >
        {v.toLocaleString()}
      </div>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex-1 px-4 py-3 text-left rounded-[var(--r-lg)] cursor-pointer',
          'bg-[var(--bg-2)] border border-[var(--bd-subtle)]',
          'shadow-[inset_0_1px_0_var(--hi-top),var(--sh-sm)]',
          'hover:border-[var(--bd-strong)] transition-colors',
        )}
      >
        {body}
      </button>
    );
  }
  return <Card className="flex-1 px-4 py-3">{body}</Card>;
}

// ----------------------------------------------------------------------------
//  List view — flat, room-grouped table with inline-editable CAT/SEL
// ----------------------------------------------------------------------------

interface ListViewProps {
  result: ParsedEstimateResponse;
  onResult: (_res: ParsedEstimateResponse) => void;
}

function ListView({ result, onResult }: ListViewProps) {
  // B-448: single-open accordion — only one source-PDF preview is expanded at a
  // time across the whole list. Clicking another row moves the preview to it;
  // clicking the open row again collapses. Hidden by default (null).
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  return (
    <Card className="overflow-auto p-0">
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr>
            <Th className="text-right w-[38px]">#</Th>
            <Th className="w-[64px]">CAT</Th>
            <Th className="w-[76px]">SEL</Th>
            <Th className="w-[44px]">Act</Th>
            <Th className="text-right w-[96px]">Qty</Th>
            <Th>Description</Th>
            <Th className="w-[110px]">Provenance</Th>
          </tr>
        </thead>
        <tbody>
          {result.estimate.rooms.map((room, roomIdx) => (
            <RoomGroup
              key={`${room.group_code}-${roomIdx}`}
              room={room}
              roomIdx={roomIdx}
              parseHash={result.parse_hash}
              onResult={onResult}
              expandedKey={expandedKey}
              onToggleExpand={setExpandedKey}
            />
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function RoomGroup({
  room,
  roomIdx,
  parseHash,
  onResult,
  expandedKey,
  onToggleExpand,
}: {
  room: ParsedEstimateResponse['estimate']['rooms'][number];
  roomIdx: number;
  parseHash: string;
  onResult: (_res: ParsedEstimateResponse) => void;
  expandedKey: string | null;
  onToggleExpand: (_key: string | null) => void;
}) {
  const label = room.group_description || room.group_code || `Room ${roomIdx + 1}`;
  const count = room.line_items.length;
  // PRP-MCP-5: "from chat" marks and the "Only chat changes" filter.
  const { index: chatIndex, onlyChat } = useChatMarks();
  if (onlyChat && !roomHasChat(chatIndex, room)) return null;
  return (
    <>
      <tr>
        <td colSpan={7} className="p-0">
          <div className="flex items-center gap-[10px] pt-3 px-[14px] pb-[6px]">
            <span className="text-[11px] font-bold tracking-[.05em] uppercase text-[var(--tx-mid)] whitespace-nowrap">
              {label}
            </span>
            {chatIndex.rooms.has(room.group_code) && <ChatTag />}
            <span className="flex-1 h-px bg-[var(--bd-subtle)]" />
            <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--tx-faint)]">
              {count} {count === 1 ? 'item' : 'items'}
            </span>
          </div>
        </td>
      </tr>
      {room.line_items.map((li, liIdx) => {
        const rowKey = `${roomIdx}-${liIdx}`;
        const fromChat = isChatLine(chatIndex, room, li);
        if (onlyChat && !fromChat) return null;
        return (
          <ListRow
            key={`${roomIdx}-${li.line_number ?? liIdx}${lineSuffixOf(li)}`}
            li={li}
            fromChat={fromChat}
            n={li.line_number ?? liIdx + 1}
            roomIdx={roomIdx}
            lineItemIndex={liIdx}
            parseHash={parseHash}
            onResult={onResult}
            isExpanded={expandedKey === rowKey}
            onToggle={() =>
              onToggleExpand(expandedKey === rowKey ? null : rowKey)
            }
          />
        );
      })}
    </>
  );
}

function ListRow({
  li,
  n,
  roomIdx,
  lineItemIndex,
  parseHash,
  onResult,
  isExpanded,
  onToggle,
  fromChat = false,
}: {
  li: ParsedLineItem;
  fromChat?: boolean;
  n: number;
  roomIdx: number;
  lineItemIndex: number;
  parseHash: string;
  onResult: (_res: ParsedEstimateResponse) => void;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const prov = (li.provenance ?? 'ORIGINAL') as Provenance;
  const pm = PROV_META[prov] ?? PROV_META.ORIGINAL;
  const sym = activitySymbol(li.activity ?? '');
  const isUnmapped = isUnmappedLine(li);

  return (
    <>
    <tr
      className={`border-b border-[var(--bd-subtle)] hover:bg-[var(--bg-2)] ${
        isExpanded ? 'bg-[var(--bg-2)]' : ''
      }`}
    >
      <td className="h-[var(--row-h)] px-3 align-middle text-right font-[var(--font-mono)] text-[var(--tx-faint)]">
        {n}
      </td>
      <td className="h-[var(--row-h)] px-3 align-middle">
        <CodeCell
          value={li.category}
          field="cat"
          accent={!isUnmapped}
          li={li}
          roomIdx={roomIdx}
          lineItemIndex={lineItemIndex}
          parseHash={parseHash}
          onResult={onResult}
        />
      </td>
      <td className="h-[var(--row-h)] px-3 align-middle">
        <CodeCell
          value={li.selector}
          field="sel"
          accent={false}
          li={li}
          roomIdx={roomIdx}
          lineItemIndex={lineItemIndex}
          parseHash={parseHash}
          onResult={onResult}
        />
      </td>
      <td className="h-[var(--row-h)] px-3 align-middle">
        <span
          className="font-[var(--font-mono)] inline-grid place-items-center w-[22px] h-[22px] rounded-[5px] bg-[var(--bg-3)] border border-[var(--bd-subtle)] text-[12px] text-[var(--tx-mid)]"
          title={ACT_LABEL[sym] || li.activity || 'Unmapped'}
        >
          {sym}
        </span>
      </td>
      <td className="h-[var(--row-h)] px-3 align-middle text-right font-[var(--font-mono)] text-[var(--tx-mid)]">
        {li.quantity != null ? li.quantity.toLocaleString() : '—'}{' '}
        <span className="text-[var(--tx-faint)] text-[11px]">{li.unit}</span>
      </td>
      <td className="h-[var(--row-h)] px-3 align-middle text-[var(--tx-hi)] font-[450]">
        <button
          type="button"
          onClick={onToggle}
          title={isExpanded ? 'Hide the source PDF line' : 'Show the source PDF line'}
          aria-expanded={isExpanded}
          className="group/desc flex w-full items-start gap-[7px] text-left"
        >
          <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            className={`mt-[3px] h-[11px] w-[11px] shrink-0 text-[var(--tx-faint)] transition-transform duration-150 group-hover/desc:text-[var(--tx-mid)] ${
              isExpanded ? 'rotate-90' : ''
            }`}
          >
            <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>
            {li.description}
            {fromChat && <ChatTag className="ml-1.5" />}
            {li.notes && li.notes.length > 0 && (
              <span className="block text-[11px] font-normal text-[var(--tx-lo)]">
                {li.notes.join(' ')}
              </span>
            )}
            {li.price_note && (
              <span className="block text-[11px] font-[550] text-[var(--accent-ink)]">
                {li.price_note}
              </span>
            )}
          </span>
        </button>
      </td>
      <td className="h-[var(--row-h)] px-3 align-middle">
        <div className="flex items-center justify-between gap-1">
          <Pill kind={pm.kind}>{pm.label}</Pill>
          <PriceNotePicker
            parseHash={parseHash}
            lines={[{ room_index: roomIdx, line_item_index: lineItemIndex }]}
            current={li.price_note}
            code={`${li.category} ${li.selector}`}
            description={li.description}
            onResult={onResult}
          />
        </div>
      </td>
    </tr>
    {isExpanded && (
      <tr>
        <td colSpan={7} className="p-0">
          <SourcePreview li={li} n={n} />
        </td>
      </tr>
    )}
    </>
  );
}

/**
 * B-448: a paper-style preview of the verbatim PDF source line, expanded below
 * a list row. White "paper", black ink, print-like serif — so the founder can
 * eyeball that the line came through correctly (description, qty, price) without
 * leaving the app or opening the PDF. Shows the raw text layer untouched.
 */
function SourcePreview({ li, n }: { li: ParsedLineItem; n: number }) {
  const render = li.source_render ?? null;
  const src = (li.source_text ?? '').trim();
  const hasRender = !!(render && render.headers?.length && render.cells?.length);

  // B-499: the source-PDF viewer is opt-in (flag default OFF) and needs both the
  // in-memory uploaded File and the line's page/bbox. Otherwise we show only the
  // B-488 table — never a broken state.
  const pdfViewerEnabled = useUiStore((s) => s.pdfViewerEnabled);
  const file = useContext(SourcePdfFileContext);
  const [viewerOpen, setViewerOpen] = useState(false);
  const canShowViewer =
    pdfViewerEnabled &&
    !!file &&
    !!render &&
    render.page != null &&
    Array.isArray(render.bbox) &&
    render.bbox.length === 4;

  return (
    <div className="px-3 pb-3 pt-1 bg-[var(--bg-2)]">
      <div
        className="rounded-[6px] border border-black/10 bg-[#fcfbf7] px-[18px] py-[14px] shadow-[0_1px_3px_rgba(0,0,0,.35)]"
        style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", serif' }}
      >
        <div className="mb-[8px] flex items-center justify-between gap-[8px]">
          <div className="flex items-center gap-[8px] text-[10px] font-semibold uppercase tracking-[.08em] text-neutral-500">
            <span className="inline-block h-[6px] w-[6px] rounded-full bg-neutral-400" />
            As it appears in the PDF
          </div>
          {canShowViewer && (
            <button
              type="button"
              onClick={() => setViewerOpen(true)}
              data-testid="open-pdf-source-viewer"
              className="inline-flex items-center gap-[5px] rounded-[5px] border border-neutral-300 bg-white px-[8px] py-[3px] text-[10.5px] font-semibold uppercase tracking-[.05em] text-neutral-600 hover:border-neutral-400 hover:text-neutral-800"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" className="h-[11px] w-[11px]">
                <path d="M2.5 3.5h11v9h-11z" fill="none" stroke="currentColor" strokeWidth="1.3" />
                <path d="M5 6.5h6M5 9h4" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
              View in source PDF
            </button>
          )}
        </div>
        {canShowViewer && viewerOpen && file && render && (
          <Suspense fallback={null}>
            <PdfSourceViewer
              file={file}
              page={render.page as number}
              bbox={render.bbox as [number, number, number, number]}
              lineLabel={render.cells?.[0]}
              onClose={() => setViewerOpen(false)}
            />
          </Suspense>
        )}
        {hasRender ? (
          <SourceTable render={render!} />
        ) : src ? (
          <pre className="whitespace-pre-wrap break-words font-[inherit] text-[13.5px] leading-[1.55] text-neutral-900">
            {src}
          </pre>
        ) : (
          <div className="text-[12.5px] italic leading-[1.5] text-neutral-500">
            The source text wasn’t captured for line {n}. Re-import this estimate
            to enable the PDF preview for its lines.
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * B-488: render the line the way the PDF lays it out — the carrier's own column
 * headers (DESCRIPTION / QTY / REMOVE / REPLACE / TAX / O&P / TOTAL …) above the
 * aligned cells, with the item's notes verbatim beneath. Description column is
 * left-aligned and takes the slack; numeric columns are right-aligned.
 */
function SourceTable({ render }: { render: SourceRender }) {
  const { headers, cells, notes } = render;
  return (
    <div className="font-[inherit] text-neutral-900">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-black/20 text-[10px] uppercase tracking-[.06em] text-neutral-500">
            {headers.map((h, i) => (
              <th
                key={i}
                className={`py-[3px] font-semibold ${
                  i === 0 ? 'text-left' : 'pl-[14px] text-right'
                }`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="align-top text-[13.5px] leading-[1.45]">
            {cells.map((c, i) => (
              <td
                key={i}
                className={`py-[6px] ${
                  i === 0
                    ? 'w-full pr-[14px] text-left'
                    : 'whitespace-nowrap pl-[14px] text-right tabular-nums'
                }`}
              >
                {c}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      {notes && notes.length > 0 && (
        <div className="mt-[8px] border-t border-black/10 pt-[8px]">
          <div className="mb-[3px] text-[9.5px] font-semibold uppercase tracking-[.08em] text-neutral-400">
            Notes
          </div>
          {notes.map((nt, i) => (
            <div
              key={i}
              className="text-[12.5px] italic leading-[1.5] text-neutral-700"
            >
              {nt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Inline-editable CAT / SEL code cell. On commit, calls the REAL
 * overrideLineItem endpoint (keyed by parse_hash + room/line indices) and
 * replaces page state with the returned ParsedEstimateResponse.
 */
function CodeCell({
  value,
  field,
  accent,
  li,
  roomIdx,
  lineItemIndex,
  parseHash,
  onResult,
}: {
  value: string;
  field: 'cat' | 'sel';
  accent: boolean;
  li: ParsedLineItem;
  roomIdx: number;
  lineItemIndex: number;
  parseHash: string;
  onResult: (_res: ParsedEstimateResponse) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const commit = async (raw: string) => {
    const next = raw.trim();
    setEditing(false);
    const current = value || '';
    if (next === current) return;
    // overrideLineItem takes both cat + sel; send the edited field and keep
    // the other unchanged.
    const cat = field === 'cat' ? next : li.category || '';
    const sel = field === 'sel' ? next : li.selector || '';
    setSaving(true);
    try {
      const res = await BackendAPI.overrideLineItem(parseHash, roomIdx, lineItemIndex, cat, sel);
      onResult(res);
    } catch {
      // leave existing state in place on failure
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        defaultValue={value || ''}
        disabled={saving}
        className="w-full h-[22px] px-[6px] rounded-[var(--r-xs)] font-[var(--font-mono)] text-[11px] bg-[var(--bg-inset)] border border-[var(--accent-line)] text-[var(--tx-hi)] outline-none shadow-[0_0_0_3px_var(--accent-soft)]"
        onBlur={(e) => void commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void commit((e.target as HTMLInputElement).value);
          if (e.key === 'Escape') setEditing(false);
        }}
      />
    );
  }

  return (
    <button
      type="button"
      title="Click to edit"
      disabled={saving}
      onClick={() => setEditing(true)}
      className={cn(
        'inline-flex items-center gap-1 h-[22px] px-[6px] rounded-[var(--r-xs)] border border-[var(--bd)] bg-[var(--bg-2)]',
        'font-[var(--font-mono)] text-[11px] tracking-[.02em] cursor-text hover:border-[var(--bd-strong)]',
        accent ? 'text-[var(--accent-ink)]' : 'text-[var(--tx-mid)]',
        saving && 'opacity-50',
      )}
    >
      {value || '—'}
    </button>
  );
}

// ----------------------------------------------------------------------------
//  Review view — B-437b: ONLY the unmapped lines, each with a code-search
//  picker. Reuses the same overrideLineItem → persist → export plumbing the
//  inline List editor uses, so an assignment here is WYSIWYG with Download-ESX.
// ----------------------------------------------------------------------------

/** B-486: a bundle of identical unmapped lines (same description) so the
 *  estimator assigns a code ONCE and it fans out to every copy. */
interface UnmappedGroup {
  key: string;
  description: string;
  rep: ParsedLineItem; // representative line (for desc/qty/source preview)
  roomLabel: string;
  refs: UnmappedRef[]; // every instance — bulk-assigned together
}

/** Collapse identical unmapped descriptions into one group. Lines with no
 *  description (or unique ones) each stand alone. */
function groupUnmapped(unmapped: UnmappedRef[]): UnmappedGroup[] {
  const map = new Map<string, UnmappedGroup>();
  for (const u of unmapped) {
    const desc = (u.li.description || '').trim();
    const key = desc ? desc.toLowerCase() : `__solo_${u.roomIdx}_${u.lineItemIndex}`;
    const existing = map.get(key);
    if (existing) existing.refs.push(u);
    else
      map.set(key, {
        key,
        description: u.li.description || '—',
        rep: u.li,
        roomLabel: u.roomLabel,
        refs: [u],
      });
  }
  return Array.from(map.values());
}

function ReviewView({
  result,
  onResult,
  flagged,
}: ListViewProps & { flagged?: Set<number> }) {
  const unmapped = useMemo(() => collectUnmapped(result, flagged), [result, flagged]);
  const groups = useMemo(() => groupUnmapped(unmapped), [unmapped]);

  if (unmapped.length === 0) {
    return (
      <Card className="px-4 py-10 text-center">
        <div className="text-[15px] font-semibold text-[var(--ok)] mb-1">All lines mapped</div>
        <div className="text-[12.5px] text-[var(--tx-lo)] max-w-[460px] mx-auto">
          Every imported line resolved to a Xactimate code. Nothing needs review before export.
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-0 overflow-visible" data-testid="review-view">
      <div className="px-4 py-3 border-b border-[var(--bd-subtle)]">
        <div className="text-[13px] font-semibold text-[var(--tx-hi)]">
          {unmapped.length} line{unmapped.length === 1 ? ' needs' : 's need'}{' '}
          {flagged && flagged.size > 0 ? 'a check' : 'a code'}
          {groups.length !== unmapped.length && (
            <span className="ml-1.5 font-normal text-[var(--tx-lo)]">· {groups.length} unique</span>
          )}
        </div>
        <div className="text-[11.5px] text-[var(--tx-lo)] mt-0.5 max-w-[680px]">
          Search a code or browse <span className="font-[var(--font-mono)]">CAT → SEL → action</span>.
          Identical lines are bundled — assign once and it applies to every copy. The verbatim PDF
          wording is kept unless you tick “Replace with standard description.” Anything you leave still
          exports as a faithful <code>*REVIEW*</code> custom line.
        </div>
      </div>
      <div>
        {groups.map((g) => (
          <ReviewGroupCard
            key={g.key}
            group={g}
            parseHash={result.parse_hash}
            onResult={onResult}
          />
        ))}
      </div>
    </Card>
  );
}

function ReviewGroupCard({
  group,
  parseHash,
  onResult,
}: {
  group: UnmappedGroup;
  parseHash: string;
  onResult: (_res: ParsedEstimateResponse) => void;
}) {
  const { rep, description, roomLabel, refs } = group;
  const count = refs.length;
  return (
    <div
      className="px-4 py-3 border-b border-[var(--bd-subtle)] last:border-0 flex flex-col gap-2"
      data-testid="review-group"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-[.05em] text-[var(--tx-faint)] mb-0.5">
            {roomLabel}
            {count > 1 && ` · ${count} identical lines`}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[13px] text-[var(--tx-hi)] font-[450]">{description || '—'}</span>
            {count > 1 && <Pill kind="neutral">×{count}</Pill>}
          </div>
          <div className="text-[11.5px] text-[var(--tx-lo)] mt-0.5 font-[var(--font-mono)]">
            {rep.quantity != null ? rep.quantity.toLocaleString() : '—'} {rep.unit}
            {rep.price_note ? <> · {rep.price_note}</> : rep.rcv != null && <> · ${rep.rcv.toLocaleString()}</>}
          </div>
        </div>
        <Pill kind="bad">Unmapped</Pill>
      </div>
      {/* B-503: show what the PDF actually imported right here in Review — the
          same paper-table reference (and "View in source PDF" button when the
          viewer is enabled) the List view shows. */}
      <SourcePreview li={rep} n={rep.line_number ?? 0} />
      <CodeAssigner refs={refs} parseHash={parseHash} onResult={onResult} />
    </div>
  );
}

/** A code staged for assignment but not yet committed (B-486 two-step). */
interface StagedCode {
  cat: string;
  sel: string;
  dsc: string;
  unit: string;
  actions: string[]; // supported action symbols
  action: string; // chosen action symbol
}

// Categories are the same for every card — fetch once, share across the page.
let _catsCache: CategoryOption[] | null = null;

/**
 * B-437b/B-486/B-511 code assigner for the Review tab. Two ways in — a debounced
 * description SEARCH or a CAT → SEL cascade browse — both STAGE a selection; the
 * estimator can then change the ACTION (the catalog's supported actions for that
 * item), optionally swap the verbatim PDF wording for the catalog's standard
 * description, then OK to commit. Commits to EVERY bundled duplicate at once
 * (`refs`) via the bulk endpoint, or the single endpoint for a lone line.
 */
function CodeAssigner({
  refs,
  parseHash,
  onResult,
}: {
  refs: UnmappedRef[];
  parseHash: string;
  onResult: (_res: ParsedEstimateResponse) => void;
}) {
  const [mode, setMode] = useState<'search' | 'browse'>('search');
  const [staged, setStaged] = useState<StagedCode | null>(null);
  const [replaceDesc, setReplaceDesc] = useState(false);
  const [saving, setSaving] = useState(false);

  // Search state
  const [q, setQ] = useState('');
  const [results, setResults] = useState<DictionarySearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  // Browse (cascade) state
  const [cats, setCats] = useState<CategoryOption[] | null>(_catsCache);
  const [cat, setCat] = useState('');
  const [selFilter, setSelFilter] = useState('');
  const [sels, setSels] = useState<SelectorOption[] | null>(null);
  const [selsLoading, setSelsLoading] = useState(false);

  // Debounced dictionary search (min 2 chars, matching the backend guard).
  useEffect(() => {
    if (mode !== 'search' || staged) return;
    const query = q.trim();
    if (query.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await BackendAPI.searchDictionary(query);
        if (!cancelled) setResults(res.results.slice(0, 12));
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, mode, staged]);

  // Load categories the first time the user browses.
  useEffect(() => {
    if (mode !== 'browse' || cats) return;
    let cancelled = false;
    BackendAPI.listCategories()
      .then((r) => {
        if (!cancelled) {
          _catsCache = r.categories;
          setCats(r.categories);
        }
      })
      .catch(() => {
        if (!cancelled) setCats([]);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, cats]);

  // Load selectors whenever a category is chosen.
  useEffect(() => {
    if (!cat) {
      setSels(null);
      return;
    }
    let cancelled = false;
    setSelsLoading(true);
    BackendAPI.listSelectors(cat)
      .then((r) => {
        if (!cancelled) setSels(r.selectors);
      })
      .catch(() => {
        if (!cancelled) setSels([]);
      })
      .finally(() => {
        if (!cancelled) setSelsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cat]);

  const stageFromSearch = async (r: DictionarySearchResult) => {
    setStaged({
      cat: r.cat,
      sel: r.sel,
      dsc: r.description,
      unit: r.unit,
      actions: r.activity_code ? [r.activity_code] : [],
      action: r.activity_code || '',
    });
    // Fetch the FULL supported action set so the action dropdown offers the
    // alternatives, not just the one the search row carried.
    try {
      const res = await BackendAPI.listActions(r.cat, r.sel);
      const syms = res.actions.map((a) => a.symbol);
      if (syms.length)
        setStaged((s) => (s ? { ...s, actions: syms, action: s.action || syms[0] } : s));
    } catch {
      /* keep the single action from the search row */
    }
  };

  const stageFromCascade = (s: SelectorOption) => {
    setStaged({
      cat,
      sel: s.sel,
      dsc: s.dsc,
      unit: s.unit,
      actions: s.actions,
      action: s.actions[0] || '',
    });
  };

  const cancelStaged = () => {
    setStaged(null);
    setReplaceDesc(false);
  };

  const commit = async () => {
    if (!staged) return;
    setSaving(true);
    try {
      const action = staged.action || undefined;
      const res =
        refs.length > 1
          ? await BackendAPI.overrideLineItemsBulk(
              parseHash,
              refs.map((r) => ({ roomIndex: r.roomIdx, lineItemIndex: r.lineItemIndex })),
              staged.cat,
              staged.sel,
              replaceDesc,
              action,
            )
          : await BackendAPI.overrideLineItem(
              parseHash,
              refs[0].roomIdx,
              refs[0].lineItemIndex,
              staged.cat,
              staged.sel,
              replaceDesc,
              action,
            );
      onResult(res);
    } catch {
      // leave the row in place on failure; the user can retry
    } finally {
      setSaving(false);
    }
  };

  // ----- Staged: pick action + replace-desc, then OK ----------------------
  if (staged) {
    const many = refs.length > 1;
    return (
      <div
        className="rounded-[var(--r-sm)] border border-[var(--accent-line)] bg-[var(--bg-inset)] p-2.5 flex flex-col gap-2"
        data-testid="assign-staged"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-[var(--font-mono)] text-[12px] text-[var(--accent-ink)] font-semibold">
            {staged.cat} {staged.sel}
          </span>
          <span className="text-[12px] text-[var(--tx-hi)]">{staged.dsc}</span>
          {staged.unit && (
            <span className="text-[11px] text-[var(--tx-faint)] font-[var(--font-mono)]">
              {staged.unit}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[11px] text-[var(--tx-lo)]">Action</label>
          <select
            data-testid="assign-action"
            value={staged.action}
            disabled={saving || staged.actions.length === 0}
            onChange={(e) => setStaged((s) => (s ? { ...s, action: e.target.value } : s))}
            className="h-[28px] px-2 rounded-[var(--r-sm)] text-[12px] bg-[var(--bg-1)] border border-[var(--bd)] text-[var(--tx-hi)] outline-none focus:border-[var(--accent-line)] disabled:opacity-50"
          >
            {staged.actions.length === 0 && <option value="">—</option>}
            {staged.actions.map((sym) => (
              <option key={sym} value={sym}>
                {sym} · {ACT_LABEL[sym] || sym}
              </option>
            ))}
          </select>
          <label className="ml-1 inline-flex items-center gap-1.5 text-[11px] text-[var(--tx-lo)] cursor-pointer">
            <input
              type="checkbox"
              data-testid="assign-replace-desc"
              checked={replaceDesc}
              disabled={saving}
              onChange={(e) => setReplaceDesc(e.target.checked)}
            />
            Replace with standard description
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="primary" disabled={saving} onClick={() => void commit()} data-testid="assign-ok">
            {saving ? 'Saving…' : many ? `Assign to all ${refs.length}` : 'Assign code'}
          </Button>
          <Button size="sm" variant="secondary" disabled={saving} onClick={cancelStaged} data-testid="assign-cancel">
            Cancel
          </Button>
          {!replaceDesc && (
            <span className="text-[10.5px] text-[var(--tx-faint)]">
              Keeps the verbatim PDF description.
            </span>
          )}
        </div>
      </div>
    );
  }

  // ----- Picker: search OR cascade browse ---------------------------------
  const filteredSels = (sels || []).filter((s) => {
    const f = selFilter.trim().toLowerCase();
    if (!f) return true;
    return s.sel.toLowerCase().includes(f) || (s.dsc || '').toLowerCase().includes(f);
  });

  return (
    <div className="flex flex-col gap-2">
      <div className="inline-flex gap-1 text-[11px]">
        <button
          type="button"
          data-testid="assign-mode-search"
          onClick={() => setMode('search')}
          className={`px-2 py-[3px] rounded-[var(--r-sm)] border ${mode === 'search' ? 'border-[var(--accent-line)] text-[var(--accent-ink)]' : 'border-[var(--bd)] text-[var(--tx-lo)]'}`}
        >
          Search
        </button>
        <button
          type="button"
          data-testid="assign-mode-browse"
          onClick={() => setMode('browse')}
          className={`px-2 py-[3px] rounded-[var(--r-sm)] border ${mode === 'browse' ? 'border-[var(--accent-line)] text-[var(--accent-ink)]' : 'border-[var(--bd)] text-[var(--tx-lo)]'}`}
        >
          Browse codes
        </button>
      </div>

      {mode === 'search' ? (
        <div className="relative">
          <input
            value={q}
            data-testid="assign-search-input"
            placeholder="Search a code by description… (e.g. “clean siding”)"
            onChange={(e) => setQ(e.target.value)}
            className="w-full h-[30px] px-[10px] rounded-[var(--r-sm)] text-[12px] bg-[var(--bg-inset)] border border-[var(--bd)] text-[var(--tx-hi)] outline-none focus:border-[var(--accent-line)]"
          />
          {(searching || results.length > 0) && (
            <div className="absolute z-20 mt-1 w-full max-h-[260px] overflow-auto rounded-[var(--r-sm)] border border-[var(--bd)] bg-[var(--bg-1)] shadow-[var(--sh-md)]">
              {searching && results.length === 0 && (
                <div className="px-3 py-2 text-[11.5px] text-[var(--tx-lo)]">Searching…</div>
              )}
              {results.map((r) => (
                <button
                  key={`${r.cat} ${r.sel} ${r.activity_code}`}
                  type="button"
                  onClick={() => void stageFromSearch(r)}
                  className="w-full text-left px-3 py-2 hover:bg-[var(--bg-2)] border-b border-[var(--bd-subtle)] last:border-0"
                >
                  <span className="font-[var(--font-mono)] text-[11.5px] text-[var(--accent-ink)]">
                    {r.cat} {r.sel}
                  </span>
                  <span className="text-[11.5px] text-[var(--tx-hi)] ml-2">{r.description}</span>
                  <span className="text-[11px] text-[var(--tx-faint)] ml-2">{r.unit}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {/* CAT dropdown */}
          <select
            data-testid="cascade-cat"
            value={cat}
            onChange={(e) => {
              setCat(e.target.value);
              setSelFilter('');
            }}
            className="h-[30px] px-2 rounded-[var(--r-sm)] text-[12px] bg-[var(--bg-inset)] border border-[var(--bd)] text-[var(--tx-hi)] outline-none focus:border-[var(--accent-line)]"
          >
            <option value="">{cats ? 'Choose a category (CAT)…' : 'Loading categories…'}</option>
            {(cats || []).map((c) => (
              <option key={c.cat} value={c.cat}>
                {c.cat}
                {c.group_description ? ` — ${c.group_description}` : ''}
              </option>
            ))}
          </select>

          {/* SEL list (filterable) — only once a CAT is picked */}
          {cat && (
            <div>
              <input
                value={selFilter}
                data-testid="cascade-sel-filter"
                placeholder="Filter selectors…"
                onChange={(e) => setSelFilter(e.target.value)}
                className="w-full h-[28px] px-[10px] mb-1 rounded-[var(--r-sm)] text-[12px] bg-[var(--bg-inset)] border border-[var(--bd)] text-[var(--tx-hi)] outline-none focus:border-[var(--accent-line)]"
              />
              <div
                data-testid="cascade-sel"
                className="max-h-[220px] overflow-auto rounded-[var(--r-sm)] border border-[var(--bd)] bg-[var(--bg-1)]"
              >
                {selsLoading && (
                  <div className="px-3 py-2 text-[11.5px] text-[var(--tx-lo)]">Loading selectors…</div>
                )}
                {!selsLoading && filteredSels.length === 0 && (
                  <div className="px-3 py-2 text-[11.5px] text-[var(--tx-lo)]">No selectors.</div>
                )}
                {filteredSels.map((s) => (
                  <button
                    key={s.sel}
                    type="button"
                    onClick={() => stageFromCascade(s)}
                    className="w-full text-left px-3 py-2 hover:bg-[var(--bg-2)] border-b border-[var(--bd-subtle)] last:border-0"
                  >
                    <span className="font-[var(--font-mono)] text-[11.5px] text-[var(--accent-ink)]">
                      {cat} {s.sel}
                    </span>
                    <span className="text-[11.5px] text-[var(--tx-hi)] ml-2">{s.dsc}</span>
                    {s.unit && (
                      <span className="text-[11px] text-[var(--tx-faint)] ml-2 font-[var(--font-mono)]">
                        {s.unit}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'sticky top-0 z-[2] bg-[var(--bg-1)] text-left text-[10.5px] font-semibold text-[var(--tx-lo)]',
        'tracking-[.03em] uppercase h-[34px] px-3 border-b border-[var(--bd)] whitespace-nowrap',
        className,
      )}
    >
      {children}
    </th>
  );
}
